'use client'

import { Button } from '@/core/components/form/button/button'
import Link from 'next/link'
import { useMemo } from 'react'
import { LuChevronRight, LuSmartphone } from 'react-icons/lu'

import DragRail from '@/core/components/optional/drag-rail'
import Skeleton from '@/core/components/skeleton'
import { cn } from '@/core/utils/common'
import AnnouncementBanner from '@/features/veluvit/components/home/announcement-banner'
import { useAnnouncement } from '@/features/veluvit/hooks/use-announcement'
import FeedCard from '@/features/veluvit/components/home/feed-card'
import HomeBanner from '@/features/veluvit/components/home/home-banner'
import {
  HomeEmpty,
  HomeError,
} from '@/features/veluvit/components/home/home-notice'
import PageHeader from '@/features/veluvit/components/shell/page-header'
import { CARD_FX } from '@/features/veluvit/components/ui/card-fx'
import { useSessionStore } from '@/features/veluvit/stores/session.store'
import { CatalogContext } from '@/features/veluvit/hooks/use-catalog'
import { useHome } from '@/features/veluvit/hooks/use-home'
import { useShowAdult } from '@/features/veluvit/hooks/use-show-adult'
import { homeWords } from '@/features/veluvit/lib/home'
import { buildLiveCatalog } from '@/features/veluvit/lib/live-catalog'
import { isAdultWork } from '@/features/veluvit/lib/rules'
import { openAppSheet } from '@/features/veluvit/services/account.actions'

import type { HomeSection as HomeSectionConfig } from '@/features/veluvit/schemas/home.schema'
import type { Work } from '@/features/veluvit/types/api.types'
import type { ProductType } from '@/features/veluvit/types/catalog.types'

const GRID =
  'grid grid-cols-2 gap-x-2.5 gap-y-5 over10:grid-cols-[repeat(auto-fill,minmax(184px,1fr))] over10:gap-x-4 over10:gap-y-7'
const H2 =
  'truncate text-c17 leading-[1.3] font-bold tracking-[-0.01em] over10:text-c18'
const SUB = 'mt-0.5 truncate text-c12 text-t3 over10:text-c13'
const SECTION = 'pt-7 over10:pt-9'

/** 어드민에서 제목을 비운 칸의 기본 제목 */
function defaultTitle(
  source: HomeSectionConfig['source'],
  words: ReturnType<typeof homeWords>
) {
  if (source === 'chats') return `대화 많은 ${words.unit}`
  if (source === 'likes') return `좋아요 많은 ${words.unit}`
  if (source === 'new') return words.fresh
  if (source === 'official') return `공식 ${words.unit}`
  if (source === 'all') return words.allTitle
  return `추천 ${words.unit}`
}

function HomeSection({
  title,
  sub,
  ids,
  ranked,
}: {
  title: string
  sub?: string
  ids: string[]
  ranked?: boolean
}) {
  if (!ids.length) return null
  return (
    <section aria-label={title} className={SECTION}>
      <div className="mb-2.5 flex items-end gap-3 px-4 over10:mb-3 over10:px-8">
        <div className="min-w-0 flex-1">
          <h2 className={H2}>{title}</h2>
          {sub && <p className={SUB}>{sub}</p>}
        </div>
        <Link
          href="/explore"
          className="inline-flex flex-none items-center text-c12 text-t3 no-underline hover:text-t1 over10:text-c13"
        >
          전체보기
          <LuChevronRight
            aria-hidden="true"
            className="size-3.5 over10:size-4"
          />
        </Link>
      </div>
      <DragRail
        ariaLabel={title}
        classNames={{ track: 'gap-2.5 px-4 pb-1.5 over10:gap-4 over10:px-8' }}
      >
        {ids.map((id, index) => (
          <FeedCard
            key={id}
            id={id}
            variant="rail"
            rank={ranked ? index + 1 : undefined}
          />
        ))}
      </DragRail>
    </section>
  )
}

function HomeGrid({
  id,
  title,
  sub,
  ids,
  ranked,
}: {
  id: string
  title: string
  sub?: string
  ids: string[]
  ranked?: boolean
}) {
  if (!ids.length) return null
  return (
    <section
      aria-labelledby={'home-' + id}
      className={cn('px-4 over10:px-8', SECTION)}
    >
      <h2 id={'home-' + id} className={cn(H2, 'mb-2.5 over10:mb-3')}>
        {title}
      </h2>
      {sub && <p className="-mt-1.5 mb-3 text-c13 text-description">{sub}</p>}
      <div data-grid className={GRID}>
        {ids.map((workId, index) => (
          <FeedCard
            key={workId}
            id={workId}
            priority={index < 4}
            rank={ranked ? index + 1 : undefined}
          />
        ))}
      </div>
    </section>
  )
}

function HomeSkeleton() {
  return (
    <div role="status" className="px-4 py-6 over10:px-8">
      <span className="sr-only">작품을 불러오고 있어요.</span>
      <div aria-hidden="true" className={GRID}>
        {['first', 'second', 'third', 'fourth'].map((key) => (
          <div key={key} className="min-w-0 space-y-3">
            <Skeleton variant="rectangular" className="aspect-[3/4] w-full" />
            <Skeleton variant="rectangular" width="72%" height={12} />
            <Skeleton variant="rectangular" width="48%" height={10} />
          </div>
        ))}
      </div>
    </div>
  )
}

/** 홈은 어드민 홈 구성(배너 · 추천 칸)만 따른다 — 칸 순서 · 제목 · 작품 · 순위 표시는 서버가 계산한 그대로. 부분 목록은 앱 전체 세션 카탈로그를 덮어쓰지 않는다. */
export default function FeedHome({ ptype }: { ptype: ProductType }) {
  const config = useSessionStore((state) => state.data?.config)
  const userName = useSessionStore((state) => state.data?.user?.name)
  const ownedCards = useSessionStore((state) => state.data?.ownedCards)
  const adult = useShowAdult()
  const W = homeWords(ptype === '스토리챗')
  const home = useHome()
  const announcement = useAnnouncement()
  const result = home.data
  const works = result?.works
  const catalog = useMemo(
    () =>
      works && config
        ? buildLiveCatalog(
            works as Work[],
            config,
            userName ?? '사용자',
            ownedCards
          )
        : null,
    [works, config, userName, ownedCards]
  )
  const visible = (ids: string[]) =>
    catalog ? ids.filter((id) => adult || !isAdultWork(catalog.chars[id])) : []
  const banners = result
    ? result.banners.filter((banner) => visible([banner.workId]).length > 0)
    : []
  const sections = result
    ? result.sections.map((section) => ({
        ...section,
        workIds: visible(section.workIds),
      }))
    : []
  const empty =
    !banners.length && sections.every((section) => !section.workIds.length)

  return (
    <div className="relative">
      <PageHeader />
      <AnnouncementBanner announcement={announcement} />
      <div className="px-4 pt-3 over10:px-8 over10:pt-6">
        <Button
          type="button"
          onClick={() => openAppSheet()}
          className={cn(
            'flex w-full cursor-pointer items-center gap-3 rounded-panel bg-well px-3.5 py-2.5 text-left transition-colors hover:bg-hover',
            CARD_FX
          )}
          variant="bare"
        >
          <span className="grid size-8 flex-none place-items-center rounded-field bg-foreground/10 text-t1">
            <LuSmartphone aria-hidden="true" className="size-4.5" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-c12 text-t3">veluvit 앱</span>
            <span className="block truncate text-c14 font-semibold text-t1">
              알림 받으며 대화를 이어 가세요
            </span>
          </span>
          <LuChevronRight
            aria-hidden="true"
            className="size-4.5 flex-none text-t3"
          />
        </Button>
      </div>
      {home.error && <HomeError onRetry={home.reload} />}
      {!result && !home.error && <HomeSkeleton />}
      {result && catalog && (
        <CatalogContext.Provider value={catalog}>
          <HomeBanner
            key={JSON.stringify(banners.map((banner) => banner.workId))}
            slides={banners.map((banner) => ({
              id: banner.workId,
              line: banner.line ?? '',
            }))}
            className="pt-4 over10:pt-6"
          />
          {empty && <HomeEmpty unit={W.unit} />}
          {sections.map((section) => {
            const title = section.title || defaultTitle(section.source, W)
            return section.source === 'all' ? (
              <HomeGrid
                key={section.id}
                id={section.id}
                title={title}
                sub={section.sub}
                ids={section.workIds}
                ranked={section.ranked}
              />
            ) : (
              <HomeSection
                key={section.id}
                title={title}
                sub={section.sub}
                ids={section.workIds}
                ranked={section.ranked}
              />
            )
          })}
        </CatalogContext.Provider>
      )}
    </div>
  )
}
